import type { JobApi } from '@/modules/jobs/types';
import type { LinkedInStatus } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';

/**
 * Where Finlay's automated outreach on a job stands with the sender's
 * LinkedIn: it sends, waits for a first connection, is paused after a
 * disconnect, or (older API) the signed-in user's own LinkedIn is missing.
 */
type OutreachState =
  | 'sending'
  | 'awaitingAccount'
  | 'disconnected'
  | 'missingAccount';

/**
 * The one place a job's outreach readiness is read. The API's
 * `outreachReadiness` knows the job owner's account, so it decides when
 * present; `null` means Finlay does not reach out on the job. Older API
 * builds omit it: the job's automation flag and the signed-in user's own
 * connection stand in then, and an unanswered lookup is not a missing account.
 */
const outreachStateOf = (
  job: Pick<JobApi, 'isReachOutAutomated' | 'outreachReadiness'>,
  linkedInStatus: LinkedInStatus,
): OutreachState | null => {
  switch (job.outreachReadiness) {
    case 'Ready':
      return 'sending';
    case 'AwaitingSenderAccount':
      return 'awaitingAccount';
    case 'SenderAccountDisconnected':
      return 'disconnected';
    case null:
      return null;
    case undefined:
      if (!job.isReachOutAutomated) return null;
      return linkedInStatus === 'disconnected' ? 'missingAccount' : 'sending';
  }
};

export interface OutreachHold {
  /** `waiting` is a setup step still to do; `blocked` stopped something that ran. */
  tone: 'blocked' | 'waiting';
  /** The job page's line, also the Outreach step's detail. */
  text: string;
  /** A few words for the job's row. */
  summary: string;
  /** The account was connected before, so the action is to reconnect it. */
  reconnect: boolean;
}

const holds: Record<Exclude<OutreachState, 'sending'>, OutreachHold> = {
  awaitingAccount: {
    tone: 'waiting',
    text: 'Outreach starts as soon as you connect LinkedIn.',
    summary: 'Waiting for LinkedIn',
    reconnect: false,
  },
  disconnected: {
    tone: 'blocked',
    text: 'Outreach is paused because your LinkedIn disconnected.',
    summary: 'Outreach paused',
    reconnect: true,
  },
  // Older API builds fail outreach without an account instead of holding it.
  missingAccount: {
    tone: 'blocked',
    text: 'Outreach is paused until you connect LinkedIn.',
    summary: 'Outreach paused',
    reconnect: false,
  },
};

/**
 * Why Finlay's outreach on a job waits for LinkedIn, with the copy the job
 * page and the job's row show; null when it is not held.
 */
export const outreachHold = (
  job: Pick<JobApi, 'isReachOutAutomated' | 'outreachReadiness'>,
  linkedInStatus: LinkedInStatus,
): OutreachHold | null => {
  const state = outreachStateOf(job, linkedInStatus);
  return state === null || state === 'sending' ? null : holds[state];
};

/**
 * How many of the user's live jobs would stop reaching out if their LinkedIn
 * disconnected: Finlay sends from the job owner's account, and only a
 * published job reaches out.
 */
export const countJobsPausedByDisconnect = (
  jobs: Pick<
    JobApi,
    'ownerId' | 'state' | 'isReachOutAutomated' | 'outreachReadiness'
  >[],
  userId: string,
  linkedInStatus: LinkedInStatus,
) =>
  jobs.filter(
    (job) =>
      job.ownerId === userId &&
      job.state === 'Published' &&
      outreachStateOf(job, linkedInStatus) === 'sending',
  ).length;
